import { useEffect, useRef, useState } from 'react';
import { getConsentDownloadUrl, EncryptedFileRecord } from '../services/consents';
import { decryptBlob, base64ToUint8 } from '../utils/encryption';
import { decodeClinicalImageToBlob } from '../utils/clinicalImageDecode';

/**
 * Which step lost the file. The viewer says different things for each: bytes we
 * could not fetch or decrypt are a *retrieval* problem, while bytes we hold but
 * cannot make sense of are a *decode* problem — and telling a clinician their
 * image is corrupt when the presigned URL simply failed is a different claim
 * about their data.
 */
export type DecryptFailureStage = 'retrieve' | 'decode';

export interface DecryptedFile {
    localId: string;
    /** null when this file could not be retrieved or decoded — see `error`. */
    blobUrl: string | null;
    /** Why this one file is unavailable. Absent on success. */
    error?: string;
    /** Which step failed. Absent on success. */
    failedAt?: DecryptFailureStage;
}

/**
 * Import a base64-encoded raw KEK as a CryptoKey for AES-GCM decryption.
 */
export async function importKekFromBase64(kekBase64: string): Promise<CryptoKey> {
    const rawKeyBytes = base64ToUint8(kekBase64);
    return crypto.subtle.importKey(
        'raw',
        rawKeyBytes,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
    );
}

/**
 * Download an encrypted file from S3 via presigned URL and decrypt it.
 */
export async function decryptFileFromS3(
    uuidInvestigation: string,
    s3Key: string,
    nonce: string,
    kek: CryptoKey
): Promise<ArrayBuffer> {
    const urlResp = await getConsentDownloadUrl(uuidInvestigation, s3Key);
    if (urlResp.status !== 200 || !urlResp.url) {
        throw new Error('Failed to get download URL');
    }

    const response = await fetch(urlResp.url);
    const encryptedData = await response.arrayBuffer();
    return decryptBlob(encryptedData, nonce, kek);
}

/**
 * React hook: downloads and decrypts a list of encrypted files from S3.
 * Returns object URLs for each decrypted file.
 */
export function useDecryptedFiles(
    uuidInvestigation: string,
    files: EncryptedFileRecord[],
    kekBase64: string | null
): { decryptedFiles: DecryptedFile[]; loading: boolean; error: string | null } {
    const [decryptedFiles, setDecryptedFiles] = useState<DecryptedFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const blobUrlsRef = useRef<string[]>([]);

    // Stabilize dependency on files array by serializing to a key
    const filesKey = files.map(f => f.uuid).join(',');

    useEffect(() => {
        if (!kekBase64 || files.length === 0) return;

        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const kek = await importKekFromBase64(kekBase64);

                const results: DecryptedFile[] = [];
                for (const file of files) {
                    // SKIN-206 — per file, not per batch. The decoder throws on
                    // bytes it cannot identify (that is the point: a corrupt
                    // clinical image must not render as a broken <img>), and one
                    // unreadable capture must not cost the clinician every other
                    // image in the visit. A failure below is recorded against the
                    // file and the loop continues; only failures outside the loop
                    // — an unusable KEK, say — are batch-level.
                    let stage: DecryptFailureStage = 'retrieve';
                    try {
                        const decrypted = await decryptFileFromS3(uuidInvestigation, file.s3Key, file.nonce, kek);
                        stage = 'decode';
                        // SKIN-43 — decode per the file's declared codec (zstd / DNG).
                        // Legacy files report no codec → the magic bytes decide.
                        const blob = await decodeClinicalImageToBlob(
                            new Uint8Array(decrypted),
                            { format: file.format },
                        );
                        const blobUrl = URL.createObjectURL(blob);
                        blobUrlsRef.current.push(blobUrl);
                        results.push({ localId: file.localId, blobUrl });
                    } catch (fileErr: any) {
                        results.push({
                            localId: file.localId,
                            blobUrl: null,
                            failedAt: stage,
                            error: fileErr?.message
                                || (stage === 'decode' ? 'Failed to decode image' : 'Failed to retrieve image'),
                        });
                    }
                }

                if (!cancelled) {
                    setDecryptedFiles(results);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setError(err.message || 'Failed to decrypt files');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
            blobUrlsRef.current = [];
        };
    }, [uuidInvestigation, filesKey, kekBase64]);

    return { decryptedFiles, loading, error };
}
